import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  acknowledgeMyCredential,
  fetchAccessGroupCatalogs,
  fetchMyAccess,
  issueMyCredential,
  revokeMyCredentials,
  type AccessGroup,
  type AccessResult,
  type IssueCommand,
  type IssuedCredential,
  type ProviderAccessView,
} from "../../api/providerAccess";
import { accessProblemKey, accessStatusKey } from "../shared/accessProblem";
import { LoadingState } from "../states";
import { useToast } from "../toast/toastContext";
import { BedrockApiGuide } from "../tools/BedrockApiGuide";
import { ApiStyles, apiStylePath } from "../tools/bedrockApi";
import { IconButton } from "../tools/IconButton";
import { CodeIcon, CopyIcon, RenewIcon, RevokeIcon, RotateIcon } from "../tools/ToolIcons";
import card from "../tools/ToolCard.module.css";
import { allowanceResetDate, formatDate } from "./formatters";
import { VendorIcon } from "./VendorIcon";
import styles from "./ProviderAccessPanel.module.css";
import { unchangedOr, usePolling } from "../../hooks/usePolling";
import { Tooltip } from "../shared/Tooltip";

/** Above this many models, a filter box helps people find theirs. */
const ModelFilterThreshold = 12;

type LoadState = { kind: "loading" } | { kind: "loaded"; view: ProviderAccessView } | { kind: "hidden" } | { kind: "error" };

/**
 * An engineer's keys at every provider that issues them through the portal (AIA-109), one card per provider on My Tools: the
 * access status, the key and its management (create, renew, rotate, revoke), the endpoint to call, and the models the person's
 * access groups allow, grouped by group, each one click to copy. A vendor whose status is `NotRequested`, or that issues no keys,
 * renders nothing.
 */
export function ProviderAccessPanel(): React.JSX.Element | null {
  const [vendors, setVendors] = useState<{ vendorId: string; groups: AccessGroup[]; endpoint: string | null }[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await fetchAccessGroupCatalogs();
      if (!cancelled && result.kind === "ok") {
        setVendors(result.response.map((vendor) => ({ vendorId: vendor.vendorId, groups: vendor.groups, endpoint: vendor.inferenceEndpoint ?? null })));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (vendors.length === 0) {
    return null;
  }

  return (
    <>
      {vendors.map((vendor) => (
        <VendorAccess key={vendor.vendorId} vendorId={vendor.vendorId} groups={vendor.groups} endpoint={vendor.endpoint} />
      ))}
    </>
  );
}

function VendorAccess({ vendorId, groups, endpoint }: { vendorId: string; groups: AccessGroup[]; endpoint: string | null }): React.JSX.Element | null {
  const { t } = useTranslation();
  const toast = useToast();
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [modelFilter, setModelFilter] = useState("");
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  // The shown-once key. It lives only here: never in browser storage, the URL, a cache or a log, and it is dropped on acknowledge and on unmount.
  const [issued, setIssued] = useState<IssuedCredential | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const keyInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => () => setIssued(null), []);

  // A quiet load refreshes the card in place and keeps what is shown when it fails.
  const load = useCallback(async (quiet = false): Promise<void> => {
    const result = await fetchMyAccess(vendorId);
    if (result.kind === "ok") {
      const next: LoadState = result.response.status === "NotRequested" ? { kind: "hidden" } : { kind: "loaded", view: result.response };
      setState((previous) => unchangedOr(previous, next));
    } else if (quiet) {
      return;
    } else if (result.kind === "notFound" || result.kind === "denied") {
      setState({ kind: "hidden" });
    } else {
      setState({ kind: "error" });
    }
  }, [vendorId]);

  useEffect(() => {
    void load();
  }, [load]);

  usePolling(() => load(true), state.kind === "loaded" && !busy);

  const run = async (command: IssueCommand): Promise<void> => {
    setBusy(true);
    setProblem(null);
    setAnnouncement("");
    const result = await issueMyCredential(vendorId, command);
    setBusy(false);
    if (result.kind === "ok") {
      // The key is shown whatever the refresh below answers: a failed, refused or hidden refresh must never take the only copy away.
      setIssued(result.response);
      setAnnouncement(t("providerAccess.key.announceIssued"));
      await load();
      return;
    }

    if (result.kind === "notFound") {
      setProblem("providerAccess.problems.error");
      return;
    }

    setProblem(accessProblemKey(result));
    if (result.kind === "conflict") {
      await load();
    }
  };

  /** Acknowledges the newest key not yet acknowledged, as the server does: the one on screen, or (`earlier`) one shown before a reload. */
  const acknowledge = async (earlier: boolean): Promise<void> => {
    setBusy(true);
    setProblem(null);
    const result: AccessResult<undefined> = await acknowledgeMyCredential(vendorId);
    setBusy(false);
    if (result.kind === "ok") {
      setIssued(null);
      setAnnouncement(t(earlier ? "providerAccess.key.announceConfirmed" : "providerAccess.key.announceAcknowledged"));
      await load();
    } else if (result.kind === "notFound") {
      setProblem("providerAccess.problems.error");
    } else {
      setProblem(accessProblemKey(result));
    }
  };

  const revoke = async (): Promise<void> => {
    setBusy(true);
    setProblem(null);
    const result = await revokeMyCredentials(vendorId);
    setBusy(false);
    setConfirmingRevoke(false);
    if (result.kind === "ok") {
      toast.show(t("providerAccess.key.announceRevoked"), "success");
      await load();
    } else if (result.kind === "notFound") {
      toast.show(t("providerAccess.problems.error"), "error");
    } else {
      toast.show(t(accessProblemKey(result)), "error");
      if (result.kind === "conflict") {
        await load();
      }
    }
  };

  const copy = async (secret: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(secret);
      setAnnouncement(t("providerAccess.key.copied"));
    } catch {
      keyInputRef.current?.select();
      setAnnouncement(t("providerAccess.key.copyFailed"));
    }
  };

  /** Copies an endpoint or a model id, which are not secret, and says so with a toast. */
  const copyText = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      toast.show(t("providerAccess.copied", { text }), "success");
    } catch {
      toast.show(t("providerAccess.copyFailed"), "error");
    }
  };

  if (state.kind === "hidden" && !issued) {
    return null;
  }

  const vendorName = t(`providerAccess.vendors.${vendorId}`, { defaultValue: vendorId });
  const headingId = `provider-access-${vendorId}`;
  const view = state.kind === "loaded" ? state.view : null;

  /** The tool card: the provider's icon, name and status, then the body. Always open, so a key on screen is never hidden. */
  const shell = (children: React.ReactNode): React.JSX.Element => (
    <section className={`${card.card} ${card.surface}`} aria-labelledby={headingId}>
      <div className={card.header}>
        <span className={card.headerIcon} aria-hidden="true">
          <VendorIcon vendorId={vendorId} />
        </span>
        <div className={card.headerText}>
          <h2 id={headingId} className={card.title}>
            {vendorName}
          </h2>
          <p className={card.subtitle}>{t("providerAccess.cardSubtitle")}</p>
        </div>
      </div>
      {children}
    </section>
  );

  // Without a view (it could not be refreshed), a key on screen offers only Acknowledge: regenerating needs the view's state.
  const secretBox = (key: IssuedCredential, offerRegenerate: boolean): React.JSX.Element => (
    <div className={styles.secretBox}>
      <label htmlFor={`${headingId}-key`} className={styles.secretLabel}>
        {t("providerAccess.key.label")}
      </label>
      <p className={styles.meta}>{t("providerAccess.key.shownOnce")}</p>
      <div className={styles.secretRow}>
        <input id={`${headingId}-key`} ref={keyInputRef} className={styles.secretInput} type="text" readOnly spellCheck={false} autoComplete="off" value={key.secret} />
        <button type="button" className="button button-primary" onClick={() => void copy(key.secret)}>
          {t("providerAccess.key.copy")}
        </button>
      </div>
      <h3 className={styles.subheading}>{t("providerAccess.key.setupTitle")}</h3>
      <pre className={styles.code}>
        <code>{setupLines(key, t("providerAccess.key.setupPlaceholder")).join("\n")}</code>
      </pre>
      {offerRegenerate && <p className={styles.meta}>{t("providerAccess.key.regenerateWarning")}</p>}
      <div className={styles.actions}>
        <button type="button" className="button button-primary" disabled={busy} onClick={() => void acknowledge(false)}>
          {t("providerAccess.key.acknowledge")}
        </button>
        {offerRegenerate && (
          <button type="button" className="button button-secondary" disabled={busy} onClick={() => void run("credential")}>
            {t("providerAccess.key.regenerate")}
          </button>
        )}
      </div>
    </div>
  );

  const feedback = (
    <>
      {problem && (
        <p role="alert" className={styles.problem}>
          {t(problem)}
        </p>
      )}
      <div aria-live="polite" className={styles.announce}>
        {announcement}
      </div>
    </>
  );

  if (state.kind !== "loaded" && issued) {
    return shell(
      <>
        {state.kind === "error" && <p className={styles.status}>{t("providerAccess.problems.refreshFailed")}</p>}
        {secretBox(issued, false)}
        {feedback}
      </>,
    );
  }

  if (state.kind === "hidden") {
    return null;
  }

  if (state.kind === "loading") {
    return shell(<LoadingState label={t("providerAccess.loading")} />);
  }

  if (view === null) {
    return shell(<p role="alert">{t("providerAccess.problems.loadFailed")}</p>);
  }

  // The groups the person holds; with none of them in the catalogue (a group since renamed), every group on offer.
  const heldGroups = groups.filter((group) => view.groups.includes(group.id));
  const shownGroups = (heldGroups.length > 0 ? heldGroups : groups).filter((group) => (group.models ?? []).length > 0);
  const models = [...new Set(shownGroups.flatMap((group) => group.models ?? []))];
  const hasAcknowledged = view.credentials.some((credential) => credential.acknowledged);
  const hasActiveAcknowledged = view.credentials.some((credential) => credential.acknowledged && credential.active);
  const hasUnacknowledged = view.credentials.some((credential) => !credential.acknowledged);
  const canIssue = view.status === "Active" || (view.status === "Restricted" && view.reason === "WaitingForAllowance");
  // A saved key that is no longer active (a switch-off deactivated it) can be replaced: the server clears dead keys before it issues.
  const canCreate = canIssue && !issued && !hasUnacknowledged && !hasActiveAcknowledged;
  const canRegenerate = canIssue && !hasAcknowledged && !issued && hasUnacknowledged;
  const canRenewOrRotate = canIssue && issued === null && hasActiveAcknowledged;
  // A key issued before a reload can no longer be shown, but it can still be acknowledged: the server acknowledges the newest key
  // not yet acknowledged, which is the one named here.
  const unconfirmed = issued
    ? undefined
    : view.credentials.filter((credential) => !credential.acknowledged).sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0];
  const canConfirm = unconfirmed !== undefined && (view.status === "Active" || view.status === "Restricted");

  // The models matching the filter, group by group; a group whose own name matches keeps all its models.
  const filter = modelFilter.trim().toLowerCase();
  const filteredGroups = shownGroups
    .map((group) => ({
      group,
      models: group.displayName.toLowerCase().includes(filter) ? (group.models ?? []) : (group.models ?? []).filter((model) => model.toLowerCase().includes(filter)),
    }))
    .filter((entry) => entry.models.length > 0);

  const canRevoke = issued === null && view.credentials.length > 0;
  const hasKeyActions = canCreate || canRegenerate || canConfirm;
  // Renew, rotate and revoke act on the person's keys as a whole, so they sit in one cell spanning every key row.
  const rowActions = !confirmingRevoke && (canRevoke || (canRenewOrRotate && (view.renewEnabled || view.rotateEnabled)));

  return shell(
    <>
      {/* Active needs no words: the key, endpoint and models below say it. Every other status explains what is held back. */}
      {view.status !== "Active" && <p className={styles.status}>{t(accessStatusKey(view))}</p>}
      {view.status === "Restricted" && view.reason === "AllowanceUsedUp" && (
        <p className={styles.status}>{t("providerAccess.status.resetsOn", { date: formatDate(allowanceResetDate()) })}</p>
      )}

      {issued && secretBox(issued, !hasAcknowledged)}

      {!issued && (
        <div className={card.section}>
          {view.credentials.length > 0 ? (
            <table className={styles.table}>
              <caption className="visually-hidden">{t("providerAccess.keys.caption")}</caption>
              <thead>
                <tr>
                  {/* The table's own heading: the first column names what every row is, so no title sits above it. */}
                  <th scope="col" className={styles.keyColumn}>
                    {t("providerAccess.keys.title")}
                  </th>
                  <th scope="col">{t("providerAccess.keys.created")}</th>
                  <th scope="col">{t("providerAccess.keys.expires")}</th>
                  <th scope="col">{t("providerAccess.keys.state")}</th>
                  {rowActions && (
                    <th scope="col">
                      <span className="visually-hidden">{t("providerAccess.keys.actions")}</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {view.credentials.map((credential, index) => (
                  <tr key={credential.credentialId}>
                    <td>
                      <code className={styles.keyId}>{credential.credentialId}</code>
                    </td>
                    <td>{formatDate(credential.createdAt.slice(0, 10))}</td>
                    <td>
                      {formatDate(credential.expiresAt.slice(0, 10))}
                      {credential.active && <span className={styles.daysLeft}>{daysLeftText(t, credential.expiresAt)}</span>}
                    </td>
                    <td>
                      <span className={`${card.chip} ${credential.active ? card.chipSuccess : card.chipNeutral}`}>
                        {credential.active ? t("providerAccess.keys.active") : t("providerAccess.keys.inactive")}
                      </span>
                    </td>
                    {rowActions && index === 0 && (
                      <td rowSpan={view.credentials.length} className={styles.rowActions}>
                        <div className={styles.actions}>
                          {canRenewOrRotate && view.renewEnabled && (
                            <IconButton label={t("providerAccess.key.renew")} description={t("providerAccess.key.renewTip")} icon={<RenewIcon />} align="end" disabled={busy} onClick={() => void run("credential/renew")} />
                          )}
                          {canRenewOrRotate && view.rotateEnabled && (
                            <IconButton label={t("providerAccess.key.rotate")} description={t("providerAccess.key.rotateTip")} icon={<RotateIcon />} align="end" disabled={busy} onClick={() => void run("credential/rotate")} />
                          )}
                          {canRevoke && (
                            <IconButton label={t("providerAccess.key.revoke")} description={t("providerAccess.key.revokeTip")} icon={<RevokeIcon />} tone="danger" align="end" disabled={busy} onClick={() => setConfirmingRevoke(true)} />
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <>
              <h3 className={`panel-title ${card.sectionTitle}`}>{t("providerAccess.keys.title")}</h3>
              <p className={card.hint}>{t("providerAccess.keys.none")}</p>
            </>
          )}

          {(canRegenerate || canConfirm) && (
            <p className={styles.notice}>
              {canRegenerate ? t("providerAccess.key.unsavedNotice") : t("providerAccess.key.unconfirmedNotice", { id: unconfirmed?.credentialId })}
            </p>
          )}

          {hasKeyActions && (
            <div className={styles.keyActions}>
              <div className={styles.actions}>
                {canCreate && (
                  <button type="button" className="button button-primary" disabled={busy} onClick={() => void run("credential")}>
                    {t("providerAccess.key.create")}
                  </button>
                )}
                {canRegenerate && (
                  <button type="button" className="button button-secondary" disabled={busy} onClick={() => void run("credential")}>
                    {t("providerAccess.key.regenerate")}
                  </button>
                )}
                {canConfirm && (
                  <button type="button" className="button button-secondary" disabled={busy} onClick={() => void acknowledge(true)}>
                    {t("providerAccess.key.confirmEarlier")}
                  </button>
                )}
              </div>
            </div>
          )}

          {canRevoke && confirmingRevoke && (
            <div className={styles.confirm}>
              <p className={styles.confirmText}>{t("providerAccess.key.revokeConfirm")}</p>
              <div className={styles.actions}>
                <button type="button" className={`button ${card.dangerButton}`} disabled={busy} onClick={() => void revoke()}>
                  {t("providerAccess.key.revokeYes")}
                </button>
                <button type="button" className="button button-secondary" disabled={busy} onClick={() => setConfirmingRevoke(false)}>
                  {t("providerAccess.key.revokeCancel")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {endpoint !== null && (
        <div className={card.section}>
          <div className={styles.sectionHead}>
            <h3 className={`panel-title ${card.sectionTitle}`}>{t("providerAccess.endpoint.title")}</h3>
            <button type="button" className={`button button-secondary ${styles.guideButton}`} onClick={() => setGuideOpen(true)}>
              <CodeIcon className={styles.buttonIcon} />
              {t("providerAccess.endpoint.examples")}
            </button>
          </div>
          <p className={card.hint}>{t("providerAccess.endpoint.hint")}</p>
          <div className={styles.codeRow}>
            <pre className={styles.code}>
              <code>{endpoint}</code>
            </pre>
            <IconButton label={t("providerAccess.endpoint.copy")} icon={<CopyIcon />} align="end" onClick={() => void copyText(endpoint)} />
          </div>
          <ul className={styles.routes} aria-label={t("providerAccess.endpoint.routes")}>
            {ApiStyles.map((style) => (
              <li key={style}>
                <span className={styles.routeName}>{t(`providerAccess.guide.styles.${style}.title`)}</span>
                <code className={styles.routePath}>{apiStylePath(style, "{model id}")}</code>
                <span className={styles.routeReach}>{t(`providerAccess.guide.styles.${style}.reach`)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {guideOpen && endpoint !== null && (
        <BedrockApiGuide endpoint={endpoint} models={models} onClose={() => setGuideOpen(false)} />
      )}

      <div className={card.section}>
        <div className={styles.sectionHead}>
          <h3 className={`panel-title ${card.sectionTitle}`}>
            {t("providerAccess.models.title")}
            {models.length > 0 && <span className={styles.count} aria-hidden="true">{models.length}</span>}
          </h3>
          {models.length > ModelFilterThreshold && (
            <input
              type="search"
              className={styles.filter}
              value={modelFilter}
              onChange={(event) => setModelFilter(event.target.value)}
              placeholder={t("providerAccess.models.filterPlaceholder")}
              aria-label={t("providerAccess.models.filter")}
            />
          )}
        </div>
        {models.length > 0 ? (
          <>
            <p className={card.hint}>{t("providerAccess.models.hint")}</p>
            {filteredGroups.length === 0 && <p className={card.hint}>{t("providerAccess.models.noMatch", { filter: modelFilter.trim() })}</p>}
            {filteredGroups.map(({ group, models: groupModels }) => (
              <section key={group.id} className={styles.modelGroup} aria-labelledby={`${headingId}-group-${group.id}`}>
                <h4 id={`${headingId}-group-${group.id}`} className={styles.modelGroupName}>
                  {group.displayName}
                  <span className={styles.count} aria-hidden="true">{groupModels.length}</span>
                </h4>
                {group.description !== "" && <p className={styles.modelGroupDescription}>{group.description}</p>}
                <ul className={styles.models}>
                  {groupModels.map((model) => (
                    <li key={model}>
                      <Tooltip label={model}>
                        <button type="button" className={styles.model} onClick={() => void copyText(model)} aria-label={t("providerAccess.models.copy", { model })}>
                          <code>{model}</code>
                          <CopyIcon className={styles.copyIcon} />
                        </button>
                      </Tooltip>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </>
        ) : (
          <p className={card.hint}>{t("providerAccess.models.none")}</p>
        )}
      </div>

      {feedback}
    </>,
  );
}

/** "78 days left" for a key's expiry, or "Expires today" on its last day. */
function daysLeftText(t: (key: string, options?: Record<string, unknown>) => string, expiresAt: string): string {
  const days = Math.ceil((Date.parse(expiresAt) - Date.now()) / 86_400_000);
  return days <= 0 ? t("providerAccess.keys.expiresToday") : t("providerAccess.keys.daysLeft", { count: days });
}

/** The environment-variable lines for a new key, with a placeholder where the key goes so the secret is not repeated. */
function setupLines(issued: IssuedCredential, placeholder: string): string[] {
  return [`${issued.secretVariableName}=${placeholder}`, ...Object.entries(issued.setup).map(([name, value]) => `${name}=${value}`)];
}
